import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ApiClient, getApiErrorMessage } from "@/services/api";
import { PageHeader, Card, Alert, LoadingSpinner, Button } from "@/components/shared";
import { formatDate } from "@/utils/format";
import { ArrowRight, ClipboardCheck, Phone, Mail, MessageSquare, MessageCircle, UserPlus } from "lucide-react";
import { LeadMessagePanel } from "@/components/agent/LeadMessagePanel";

// ─── Removed dummy SendMessageDialog — using LeadMessagePanel ────────────────

interface LeadContact {
  name?: string;
  email?: string;
  phone?: string;
  consented_at?: string;
}

interface LeadResponse {
  valuation_min: number;
  valuation_max: number;
  demand_level: string;
  strategy_summary: string;
  commission_rate: number | null;
  comparable_sales: unknown[];
}

interface LeadRequest {
  suburb: string;
  province: string;
  property_type: string;
  price_band_min: number;
  price_band_max: number;
  bedrooms: number | null;
  timeline: string;
  seller_notes: string | null;
}

export interface Lead {
  id: string;
  request_id: string;
  response_id: string;
  status: string;
  lead_status: string | null;
  unlock_fee_cents: number;
  invited_at: string;
  responded_at: string | null;
  expires_at: string;
  request: LeadRequest;
  my_response: LeadResponse;
  contact: LeadContact | null;
  shared_contacts?: Array<{ id: string; method: string; value: string; shared_at: string }>;
}

const TIMELINE_LABELS: Record<string, string> = {
  asap: "ASAP",
  this_month: "This month",
  three_to_six: "3–6 months",
  exploring: "Just exploring",
};

const DEMAND_COLORS: Record<string, string> = {
  low: "bg-white/10 text-white/60",
  medium: "bg-amber-900/40 text-amber-400",
  high: "bg-green-900/40 text-green-400",
};

function formatCurrency(cents: number): string {
  return `R ${(cents / 100).toLocaleString("en-ZA", { minimumFractionDigits: 0 })}`;
}

export function selectVisibleActiveLeads(leads: Lead[], now = new Date()): Lead[] {
  const sevenDaysAgo = new Date(now);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  return leads
    .filter((lead) => lead.status === "accepted" && lead.lead_status !== "blocked")
    .filter((lead) => {
      if (lead.lead_status === "expired") return false;
      if (lead.lead_status === "completed" && lead.responded_at && new Date(lead.responded_at) < sevenDaysAgo) return false;
      return true;
    });
}

export function countPendingInvitations(leads: Lead[], now = new Date()): number {
  return leads.filter((lead) => lead.status === "pending" && new Date(lead.expires_at) >= now).length;
}

export default function ActiveLeadsPage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editMin, setEditMin] = useState("");
  const [editMax, setEditMax] = useState("");
  const [saving, setSaving] = useState(false);
  const [messageLead, setMessageLead] = useState<string | null>(null);
  const [requestingContact, setRequestingContact] = useState<string | null>(null);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const api = new ApiClient();
        const data = await api.get<{ data: Lead[] }>("/agent-invitations");
        const visible = selectVisibleActiveLeads(data.data);
        setPendingInvitations(countPendingInvitations(data.data));

        // Fetch shared contacts for each lead
        const withContacts = await Promise.all(
          visible.map(async (lead) => {
            try {
              const shared = await api.get<{ data: Array<{ id: string; method: string; value: string; shared_at: string }> }>(`/leads/${lead.id}/contact`);
              return { ...lead, shared_contacts: shared.data };
            } catch {
              return lead;
            }
          })
        );

        setLeads(withContacts);
      } catch (err: any) {
        if (!err.message?.includes("404")) setError(getApiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, []);

  function formatEditCurrency(value: string): string {
    const num = parseInt(value.replace(/[^0-9]/g, ""), 10) || 0;
    return num ? num.toLocaleString("en-ZA") : "";
  }

  function parseEditCurrency(text: string): string {
    return String(parseInt(text.replace(/[^0-9]/g, ""), 10) || 0);
  }

  const startEdit = (lead: Lead) => {
    setEditingId(lead.response_id);
    setEditMin(String(lead.my_response.valuation_min));
    setEditMax(String(lead.my_response.valuation_max));
  };

  const saveValuation = async (lead: Lead) => {
    setSaving(true);
    try {
      const api = new ApiClient();
      await api.patch(`/responses/${lead.response_id}`, {
        valuation_min: parseInt(editMin, 10) || undefined,
        valuation_max: parseInt(editMax, 10) || undefined,
      });
      // Update local state
      setLeads((prev) =>
        prev.map((l) =>
          l.response_id === lead.response_id
            ? {
                ...l,
                my_response: {
                  ...l.my_response,
                  valuation_min: parseInt(editMin, 10) || l.my_response.valuation_min,
                  valuation_max: parseInt(editMax, 10) || l.my_response.valuation_max,
                },
              }
            : l
        )
      );
      setEditingId(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <PageHeader
        title="Active Leads"
        subtitle={`${leads.length} accepted invitation${leads.length !== 1 ? "s" : ""}`}
      />

      {error && <Alert type="error" className="mb-4">{error}</Alert>}

      {leads.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-white/50 mb-2">
              {pendingInvitations > 0 ? `${pendingInvitations} pending invitation${pendingInvitations !== 1 ? "s" : ""}` : "No active leads yet"}
            </p>
            <p className="text-sm text-white/30 max-w-xl mx-auto">
              {pendingInvitations > 0
                ? "You were selected by an owner. Accept the invitation from your dashboard to turn it into an active lead."
                : "When you accept an owner's invitation, the lead will appear here."}
            </p>
            {pendingInvitations > 0 && (
              <Link to="/agent" className="btn-primary inline-flex items-center gap-2 mt-5">
                Review invitation
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {leads.map((lead) => (
            <Card key={lead.id} className="space-y-4">
              {/* Header: Property info */}
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-white text-lg">
                    {lead.request.suburb}, {lead.request.province.replace(/_/g, " ")}
                  </h3>
                  <p className="text-sm text-white/50">
                    {lead.request.property_type.replace(/_/g, " ")} •{" "}
                    {lead.request.bedrooms ? `${lead.request.bedrooms} bed • ` : ""}
                    {TIMELINE_LABELS[lead.request.timeline] || lead.request.timeline}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  lead.lead_status === "completed" ? "bg-emerald-900/40 text-emerald-400" :
                  lead.lead_status === "contact_shared" ? "bg-blue-900/40 text-blue-400" :
                  lead.lead_status === "expired" ? "bg-white/10 text-white/40" :
                  "bg-green-900/40 text-green-400"
                }`}>
                  {lead.lead_status === "completed" ? "Appointed" :
                   lead.lead_status === "contact_shared" ? "Contact Shared" :
                   lead.lead_status === "expired" ? "Expired" :
                   "Active Lead"}
                </span>
              </div>

              {/* Price band */}
              <div className="flex gap-4 text-sm">
                <div>
                  <span className="text-white/40">Owner price band: </span>
                  <span className="text-white font-medium">
                    {formatCurrency(lead.request.price_band_min * 100)} – {formatCurrency(lead.request.price_band_max * 100)}
                  </span>
                </div>
              </div>

              {/* Seller notes */}
              {lead.request.seller_notes && (
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-xs text-white/40 mb-1">Seller Notes</p>
                  <p className="text-sm text-white/70">{lead.request.seller_notes}</p>
                </div>
              )}

              {/* Shared contact details */}
              {lead.shared_contacts && lead.shared_contacts.length > 0 && (
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-medium text-blue-400 flex items-center gap-1.5"><ClipboardCheck className="h-3.5 w-3.5" /> Owner shared contact details</p>
                  <div className="flex flex-wrap gap-3">
                    {lead.shared_contacts.map((sc) => (
                      <a
                        key={sc.id}
                        href={sc.method === "email" ? `mailto:${sc.value}` : sc.method === "whatsapp" ? `https://wa.me/${sc.value.replace(/\D/g, "")}` : `tel:${sc.value}`}
                        className="flex items-center gap-1.5 text-sm text-blue-300 hover:text-blue-200 transition-colors"
                        target={sc.method === "whatsapp" ? "_blank" : undefined}
                        rel={sc.method === "whatsapp" ? "noopener noreferrer" : undefined}
                      >
                        {sc.method === "phone" && <Phone className="h-3.5 w-3.5" />}
                        {sc.method === "email" && <Mail className="h-3.5 w-3.5" />}
                        {sc.method === "whatsapp" && <MessageSquare className="h-3.5 w-3.5" />}
                        <span>{sc.value}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact actions */}
              <div className="flex gap-2">
                <Button variant="primary" onClick={() => setMessageLead(messageLead === lead.id ? null : lead.id)} className="text-sm">
                  <MessageCircle className="h-4 w-4" /> {messageLead === lead.id ? "Hide Messages" : "Messages"}
                </Button>
                {!lead.shared_contacts?.length && lead.lead_status !== "contact_shared" && (
                  <Button
                    variant="secondary"
                    className="text-sm"
                    disabled={requestingContact === lead.id}
                    onClick={async () => {
                      setRequestingContact(lead.id);
                      try {
                        const api = new ApiClient();
                        await api.post("/contacts/request", { lead_id: lead.id });
                        // Open messages to show the system message
                        setMessageLead(lead.id);
                      } catch (err: any) {
                        const msg = err?.body?.error?.message || err.message;
                        setError(msg);
                      } finally {
                        setRequestingContact(null);
                      }
                    }}
                  >
                    <UserPlus className="h-4 w-4" /> Request Contact
                  </Button>
                )}
              </div>

              {/* Inline message panel */}
              {messageLead === lead.id && (
                <LeadMessagePanel
                  leadId={lead.id}
                  leadStatus={(lead as any).lead_status}
                  onClose={() => setMessageLead(null)}
                />
              )}

              {/* My valuation */}
              <div className="border-t border-white/10 pt-3">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs text-white/40 font-medium">My Valuation</p>
                  {editingId !== lead.response_id && (
                    <button
                      onClick={() => startEdit(lead)}
                      className="text-xs text-teal-400 hover:text-teal-300 transition-colors"
                    >
                      Edit
                    </button>
                  )}
                </div>

                {editingId === lead.response_id ? (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 text-sm">R</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formatEditCurrency(editMin)}
                        onChange={(e) => setEditMin(parseEditCurrency(e.target.value))}
                        className="w-32 pl-6 px-2 py-1 bg-white/5 border border-white/20 rounded text-sm text-white"
                        placeholder="1,500,000"
                      />
                    </div>
                    <span className="text-white/30">–</span>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40 text-sm">R</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formatEditCurrency(editMax)}
                        onChange={(e) => setEditMax(parseEditCurrency(e.target.value))}
                        className="w-32 pl-6 px-2 py-1 bg-white/5 border border-white/20 rounded text-sm text-white"
                        placeholder="2,000,000"
                      />
                    </div>
                    <Button variant="primary" onClick={() => saveValuation(lead)} disabled={saving} className="text-xs px-3 py-1">
                      Save
                    </Button>
                    <button onClick={() => setEditingId(null)} className="text-xs text-white/40 hover:text-white/60">
                      Cancel
                    </button>
                  </div>
                ) : lead.my_response.valuation_min > 0 && lead.my_response.valuation_max > 0 ? (
                  <div className="flex items-center gap-4">
                    <span className="text-white font-medium">
                      {formatCurrency(lead.my_response.valuation_min * 100)} – {formatCurrency(lead.my_response.valuation_max * 100)}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${DEMAND_COLORS[lead.my_response.demand_level] || "bg-white/10 text-white/60"}`}>
                      {lead.my_response.demand_level} demand
                    </span>
                    {lead.my_response.commission_rate && (
                      <span className="text-xs text-white/40">{lead.my_response.commission_rate}% commission</span>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <span className="text-white/40 italic text-sm">No valuation provided</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${DEMAND_COLORS[lead.my_response.demand_level] || "bg-white/10 text-white/60"}`}>
                      {lead.my_response.demand_level} demand
                    </span>
                    {lead.my_response.commission_rate && (
                      <span className="text-xs text-white/40">{lead.my_response.commission_rate}% commission</span>
                    )}
                  </div>
                )}
              </div>

              {/* Accepted date */}
              <p className="text-xs text-white/30">
                Accepted {lead.responded_at ? formatDate(lead.responded_at) : "—"}
              </p>
            </Card>
          ))}
        </div>
      )}

      {/* Messages are now inline per-lead */}
    </div>
  );
}
